import { z } from 'zod';
import {
    Bucket,
    QuestionType,
    BooleanDisplayFormat,
    AudioType,
} from '../../entities/quiz';
import { StudentGender, College } from '../../entities/user';
import {
    ArticleStatus,
    ArticleAssignmentBucket,
    ArticleContentSchema,
} from '../../entities/article';
import { ReminderContentSchema } from '../../entities/notifications';
import {
    PaginationInputSchema,
    ObjectIdSchema,
    ImageFileExtensionSchema,
} from '../common';

// Answer props schemas
const BooleanAnswerPropsSchema = z.object({
    type: z.literal(QuestionType.Boolean),
    props: z.object({
        displayFormat: z.nativeEnum(BooleanDisplayFormat),
    }),
});

const NumericAnswerPropsSchema = z.object({
    type: z.literal(QuestionType.Numeric),
    props: z.object({
        min: z.number(),
        max: z.number(),
        useStrictMax: z.boolean().optional(),
        units: z.string().optional(),
    }),
});

const PercentageAnswerPropsSchema = z.object({
    type: z.literal(QuestionType.Percentage),
    props: z.object({}),
});

const SingleChoiceAnswerPropsSchema = z.object({
    type: z.literal(QuestionType.SingleChoice),
    props: z.object({
        options: z.array(z.string()).min(1),
    }),
});

export const AnswerPropsSchema = z.discriminatedUnion('type', [
    BooleanAnswerPropsSchema,
    NumericAnswerPropsSchema,
    PercentageAnswerPropsSchema,
    SingleChoiceAnswerPropsSchema,
]);

// Actual values schemas (real-world statistics by student gender)
const NumericActualValuesSchema = z.object({
    type: z.literal(QuestionType.Numeric),
    values: z
        .object({
            [StudentGender.son]: z.number(),
            [StudentGender.daughter]: z.number(),
            [StudentGender.preferNotToAnswer]: z.number(),
        })
        .partial(),
});

const PercentageActualValuesSchema = z.object({
    type: z.literal(QuestionType.Percentage),
    values: z
        .object({
            [StudentGender.son]: z.number().min(0).max(100),
            [StudentGender.daughter]: z.number().min(0).max(100),
            [StudentGender.preferNotToAnswer]: z.number().min(0).max(100),
        })
        .partial(),
});

const BooleanActualValuesSchema = z.object({
    type: z.literal(QuestionType.Boolean),
    values: z
        .object({
            [StudentGender.son]: z.boolean(),
            [StudentGender.daughter]: z.boolean(),
            [StudentGender.preferNotToAnswer]: z.boolean(),
        })
        .partial(),
});

export const ActualValuesSchema = z.discriminatedUnion('type', [
    NumericActualValuesSchema,
    PercentageActualValuesSchema,
    BooleanActualValuesSchema,
]);

// Results props schemas (for bucket questions)
const BooleanResultsPropsSchema = z.object({
    type: z.literal(QuestionType.Boolean),
    props: z.object({
        trueValue: z.nativeEnum(Bucket),
        falseValue: z.nativeEnum(Bucket),
    }),
});

const NumericResultsPropsSchema = z.object({
    type: z.literal(QuestionType.Numeric),
    props: z.object({
        threshold: z.number(),
        belowThreshold: z.nativeEnum(Bucket),
        aboveOrEqualThreshold: z.nativeEnum(Bucket),
    }),
});

const PercentageResultsPropsSchema = z.object({
    type: z.literal(QuestionType.Percentage),
    props: z.object({
        threshold: z.number().min(0).max(100),
        belowThreshold: z.nativeEnum(Bucket),
        aboveOrEqualThreshold: z.nativeEnum(Bucket),
    }),
});

const SingleChoiceResultsPropsSchema = z.object({
    type: z.literal(QuestionType.SingleChoice),
    props: z.object({
        optionToBucketMap: z.record(z.string(), z.nativeEnum(Bucket)),
    }),
});

export const ResultsPropsSchema = z.discriminatedUnion('type', [
    BooleanResultsPropsSchema,
    NumericResultsPropsSchema,
    PercentageResultsPropsSchema,
    SingleChoiceResultsPropsSchema,
]);

export const AddQuizInputSchema = z.object({
    number: z.number().int().positive(),
    title: z.string().optional(),
});

export const UpdateQuizInputSchema = z.object({
    id: z.string(),
    title: z.string().optional(),
    titleImage: z.string().optional(), // filename; empty string means delete
    videos: z
        .object({
            [Bucket.One]: z.string(),
            [Bucket.Two]: z.string(),
        })
        .partial()
        .optional(),
});

export const PublishQuizInputSchema = z.object({
    id: z.string(),
});

export const DeleteQuizInputSchema = z.object({
    id: z.string(),
});

export const GetQuizInputSchema = z.object({
    id: z.string(),
});

export const DeleteQuizVideoInputSchema = z.object({
    id: z.string(),
    bucket: z.nativeEnum(Bucket),
});

const AudioInputSchema = z.object({
    fileName: z.string(),
    transcript: z.string().optional(),
});

export const AddQuestionInputSchema = z.object({
    quizId: z.string(),
    number: z.number().int().positive(),
    question: z.string().min(1, 'Question text is required'),
    type: z.nativeEnum(QuestionType),
    answerProps: AnswerPropsSchema.optional(),
    actualValues: ActualValuesSchema.optional(),
    isBucketQuestion: z.boolean().optional(),
    resultsProps: ResultsPropsSchema.optional(),
    image: z.string().optional(),
    audios: z
        .object({
            [AudioType.Correct]: AudioInputSchema,
            [AudioType.Incorrect]: AudioInputSchema,
            [AudioType.Underestimated]: AudioInputSchema,
            [AudioType.Overestimated]: AudioInputSchema,
        })
        .partial()
        .optional(),
    pairWithResultNumber: z.number().int().positive().optional(),
    hideFromResults: z.boolean().optional(),
});

export const UpdateQuestionInputSchema = z.object({
    quizId: z.string(),
    questionId: z.string(),
    number: z.number().int().positive().optional(),
    question: z.string().min(1, 'Question text is required').optional(),
    type: z.nativeEnum(QuestionType).optional(),
    answerProps: AnswerPropsSchema.optional(),
    actualValues: ActualValuesSchema.optional(),
    isBucketQuestion: z.boolean().optional(),
    resultsProps: ResultsPropsSchema.optional(),
    image: z.string().optional(), // Empty string means delete the image
    audios: z
        .object({
            [AudioType.Correct]: AudioInputSchema,
            [AudioType.Incorrect]: AudioInputSchema,
            [AudioType.Underestimated]: AudioInputSchema,
            [AudioType.Overestimated]: AudioInputSchema,
        })
        .partial()
        .optional(),
    pairWithResultNumber: z.number().int().positive().optional(),
    hideFromResults: z.boolean().optional(),
});

export const DeleteQuestionInputSchema = z.object({
    quizId: z.string(),
    questionId: z.string(),
});

export const GetUsersPageInputSchema = PaginationInputSchema.extend({
    searchString: z.string().optional(),
});

// ─── Article Input Schemas ────────────────────────────────────────────────────

export const ModuleAssignmentInputSchema = z.object({
    moduleNumber: z.number().int().positive(),
    bucket: z.nativeEnum(ArticleAssignmentBucket),
    order: z.number().int().min(0).default(0),
});

export const AddArticleInputSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    imageUrl: z.string().url().optional(),
    titleColor: z.string().optional(),
    shortDescription: z.string().optional(),
    content: ArticleContentSchema.optional(),
    status: z.nativeEnum(ArticleStatus).optional(),
    moduleAssignments: z.array(ModuleAssignmentInputSchema).optional(),
    college: z.nativeEnum(College).optional(),
});

export const UpdateArticleInputSchema = z.object({
    id: z.string(),
    title: z.string().min(1, 'Title is required').optional(),
    imageUrl: z.string().url().optional().nullable(),
    titleColor: z.string().optional().nullable(),
    shortDescription: z.string().optional().nullable(),
    content: ArticleContentSchema.optional().nullable(),
    status: z.nativeEnum(ArticleStatus).optional(),
    moduleAssignments: z.array(ModuleAssignmentInputSchema).optional(),
    college: z.nativeEnum(College).optional().nullable(),
});

export const DeleteArticleInputSchema = z.object({
    id: z.string(),
});

export const GetArticleInputSchema = z.object({
    id: z.string(),
});

export const GetArticlesInputSchema = z.object({
    moduleNumber: z.number().int().positive().optional(),
});

export const ArticleImageUploadInputSchema = z.object({
    articleId: ObjectIdSchema,
    fileExtension: ImageFileExtensionSchema,
});

export const DeleteArticleImagesInputSchema = z.object({
    articleId: ObjectIdSchema,
    imageUrls: z.array(z.string().url()).max(200),
});

// ─── Notification Input Schemas ──────────────────────────────────────────────

export const UpdateNotificationConfigInputSchema = z.object({
    remindersEnabled: z.boolean().optional(),
    cronExpression: z.string().min(1).optional(),
    cronDescription: z.string().min(1).optional(),
    useCustomReminderContent: z.boolean().optional(),
    customQuizReminder: ReminderContentSchema.optional(),
    customArticleReminder: ReminderContentSchema.optional(),
});

export const CreateCustomNotificationInputSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    body: z.string().min(1, 'Body is required'),
    targetColleges: z
        .array(z.nativeEnum(College))
        .min(1, 'At least one target group is required'),
});

export const UpdateCustomNotificationInputSchema = z.object({
    id: z.string(),
    title: z.string().min(1).optional(),
    body: z.string().min(1).optional(),
    targetColleges: z.array(z.nativeEnum(College)).min(1).optional(),
});

export const DeleteCustomNotificationInputSchema = z.object({
    id: z.string(),
});

export const SendCustomNotificationInputSchema = z.object({
    id: z.string(),
});

export const SendTestNotificationInputSchema = z.object({
    email: z.string().email(),
    title: z.string().min(1),
    body: z.string().min(1),
});

// ─── Campus Resource Input Schemas ────────────────────────────────────────────

export const AddCampusResourceInputSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    university: z.nativeEnum(College),
    link: z.string().url('Link must be a valid URL'),
    order: z.number().int().min(0).default(0),
});

export const UpdateCampusResourceInputSchema = z.object({
    id: z.string(),
    title: z.string().min(1, 'Title is required').optional(),
    university: z.nativeEnum(College).optional(),
    link: z.string().url('Link must be a valid URL').optional(),
    order: z.number().int().min(0).optional(),
});

export const DeleteCampusResourceInputSchema = z.object({
    id: z.string(),
});

export const GetCampusResourceInputSchema = z.object({
    id: z.string(),
});

export const GetCampusResourcesInputSchema = z.object({
    university: z.nativeEnum(College).optional(),
});
